import {FALLBACK_LANGUAGE, i18n} from '@online-offer/i18n';

import {useLocation, useParams} from 'react-router-dom';

import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {
  AlphaCatalogue,
  useGetAllApiEnumsQuery,
  useGetVehicleAuditQuery,
  VehicleAudit,
} from '../api/digitalCertificateApi';
import {useGetBusinessCaseOnlineOfferQuery, useListLogosQuery} from '../api/onlineOfferApi';
import {BusinessCaseOnlineOfferResponseBody} from '../api/types/onlineOfferTypes';
import {queryParams} from '../consts/queryParams';
import {getOnlineOfferSessionId} from '../utils/getOnlineOfferSessionId';
import {getWorkspaceFromUri} from '../utils/getWorkspaceFromUri';
import {isOnlineOfferPdfRoute} from '../utils/isOnlineOfferPdfRoute';
import {useQueryState} from '../utils/useQueryState';

const {workspace} = getWorkspaceFromUri();

type GetOnlineOfferDataContext = Partial<BusinessCaseOnlineOfferResponseBody> & {
  alphaCatalogue: AlphaCatalogue | undefined;
  vehicleAudit: VehicleAudit | Nullish;
  tenantLogoUrl: string | Nullish;
};

const logosKeys = {
  digitalDeal: 'tenant-logo_digital-deal',
  document: 'tenant-logo_document',
  email: 'tenant-logo_email',
};

export function useGetOnlineOfferData(): GetOnlineOfferDataContext {
  const [language] = useQueryState(queryParams.LNG);
  const [openedBy] = useQueryState(queryParams.OPENED_BY);
  const params = useParams<{offerId: string}>();
  const location = useLocation();
  const sessionId = isOnlineOfferPdfRoute(location.pathname)
    ? null
    : getOnlineOfferSessionId({
        offerId: params.offerId ?? '',
        openedBy: openedBy ?? null,
      });

  const {data: listLogos} = useListLogosQuery(
    {'X-Workspace': workspace ?? ''},
    {skip: isNilOrEmpty(workspace)}
  );

  const logoUploadedByTenant = listLogos?.logos?.[logosKeys.digitalDeal]?.uri;

  const {data: onlineOfferData, isLoading: isOnlineOfferQueryLoading} =
    useGetBusinessCaseOnlineOfferQuery({
      offerId: params.offerId ?? '',
      workspace: workspace ?? '',
      openedBy: openedBy ?? null,
      sessionId,
    });

  const {data: alphaCatalogue} = useGetAllApiEnumsQuery(
    {
      lang: [language ?? i18n.resolvedLanguage ?? FALLBACK_LANGUAGE],
      vehicleType: onlineOfferData?.vehicle?.type ? [onlineOfferData.vehicle.type] : undefined,
    },
    {skip: isOnlineOfferQueryLoading}
  );

  const {data: vehicleAudit} = useGetVehicleAuditQuery(
    {auditId: onlineOfferData?.carAuditId ?? '', workspace: workspace ?? ''},
    {skip: isNilOrEmpty(onlineOfferData?.carAuditId)}
  );

  return {...onlineOfferData, alphaCatalogue, vehicleAudit, tenantLogoUrl: logoUploadedByTenant};
}
